import {
  IconBook2,
  IconHeadphones,
  IconLock,
  IconLockOpen,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import BookMarkFillIcon from "../../assets/icons/bookmark-fill.svg?react";
import BookMarkSimpleIcon from "../../assets/icons/bookmark-simple.svg?react";
import ShareV2Icon from "../../assets/icons/share-v2.svg?react";
import { cn } from "../../lib/utils";
import { Tag, type TagVariant } from "./Tag";
import { SourceLabel } from "./SourceLabel";

// ── Types ───────────────────────────────────────────────────────────────

type Recommendation =
  | { reason: "fromRoom"; topic: string; logoUrl: string | null }
  | { reason: "follow"; topic: string }
  | { reason: "specialty"; topic: string }
  | { reason: "meshInterest"; topic: string }
  | { reason: "embeddingSimilar" }
  | { reason: "landmark" }
  | { reason: "exploration" }
  | { reason: "recommendedForYou" };

type Access = "open" | "closed";

type PublicationCardProps = {
  title: string;
  journalName: string | null;
  author: string;
  date: string;
  meta: string;
  /** Color of the `meta` chip — e.g. article-type hue via getArticleTypeVariant. */
  metaVariant: TagVariant;
  access: Access;
  recommendation: Recommendation | null;
  isNew: boolean;
  /** The audio player has this article loaded, playing or paused (Figma "Playing"). */
  isInPlayer: boolean;
  isBookmarked: boolean;
  onBookmark: (() => void) | null;
  onShare: (() => void) | null;
  onClick: (() => void) | null;
  className?: string;
  /** Full-width hero image above the journal line. Hidden when null. */
  imageUrl: string | null;
  /** AI-generated summary shown below the title. Hidden when null. */
  description: string | null;
  /** When set, the journal name becomes a link (→ JournalPage). */
  onJournalClick: (() => void) | null;
  /** When set, the recommendation topic becomes a link (→ the room). */
  onRecommendationClick: (() => void) | null;
};

// ── Subparts ────────────────────────────────────────────────────────────

function useRecommendationLabel(reason: Recommendation["reason"]): string {
  // Each call passes a string literal so the i18n scanner can extract it.
  // A `recommendationLabel[reason]` indirection would break extraction
  // (eslint custom rule no-restricted-syntax catches it).
  const { t } = useTranslation();
  switch (reason) {
    case "fromRoom":
      return t("From");
    case "follow":
      return t("Because you follow");
    case "specialty":
      return t("Based on your specialty");
    case "meshInterest":
      return t("Based on your interest in");
    case "embeddingSimilar":
      return t("Recommended for you");
    case "landmark":
      return t("Essential reading");
    case "exploration":
      return t("Discover something new");
    case "recommendedForYou":
      return t("Recommended for you");
    default:
      throw new Error(reason satisfies never);
  }
}

function RecommendationBadge({
  value,
  onClick,
}: {
  value: Recommendation;
  onClick: (() => void) | null;
}) {
  const label = useRecommendationLabel(value.reason);
  const topic =
    value.reason === "recommendedForYou" ||
    value.reason === "embeddingSimilar" ||
    value.reason === "landmark" ||
    value.reason === "exploration"
      ? null
      : value.topic;
  const roomLogoUrl = value.reason === "fromRoom" ? value.logoUrl : null;
  // The room topic reads in a softened title gray; other recommendation
  // reasons keep the mint eyebrow accent.
  const topicColorCls =
    value.reason === "fromRoom" ? "text-ds-primary" : "text-ds-primary";
  const roomLogo =
    roomLogoUrl === null ? null : (
      <img
        src={roomLogoUrl}
        alt=""
        className="size-6 rounded-lg object-cover shrink-0 border border-ds-black/8"
      />
    );
  return (
    <div className="flex flex-col items-start w-full min-w-0">
      <div className="flex gap-1 items-center text-footnote w-full min-w-0">
        {/* The logo shares the recommendation link when one is set. */}
        {roomLogo === null ? null : onClick === null ? (
          roomLogo
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            className="flex shrink-0 cursor-pointer"
          >
            {roomLogo}
          </button>
        )}
        <span className="font-normal text-ds-slate-600 whitespace-nowrap shrink-0 ml-1">
          {label}
        </span>
        {topic === null ? null : onClick === null ? (
          <span className={cn("font-semibold truncate min-w-0", topicColorCls)}>
            {topic}
          </span>
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            className={cn(
              "font-semibold truncate min-w-0 cursor-pointer hover:underline",
              topicColorCls
            )}
          >
            {topic}
          </button>
        )}
      </div>
      {/* Medium-style straight connector under the eyebrow, tying it to the
          card content. Aligned under the room logo when present. */}
      <div
        className={cn(
          "h-3.5 w-px bg-ds-black/8 mt-2",
          roomLogoUrl === null ? null : "ml-3"
        )}
      />
    </div>
  );
}

function AccessBadge({ access }: { access: Access }) {
  const Icon = access === "open" ? IconLockOpen : IconLock;
  return (
    <span className="inline-flex items-center justify-center size-5 mb-0.5 p-1">
      <Icon
        stroke={1.5}
        className={
          access === "closed" ? "text-ds-lemon-600" : "text-ds-mint-700"
        }
      />
    </span>
  );
}

function ActionRow({
  isBookmarked,
  onBookmark,
  onShare,
}: {
  isBookmarked: boolean;
  onBookmark: (() => void) | null;
  onShare: (() => void) | null;
}) {
  const { t } = useTranslation();
  if (onBookmark === null && onShare === null) return null;
  return (
    <div className="flex h-10 items-center justify-end gap-2 shrink-0">
      {onShare === null ? null : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onShare();
          }}
          aria-label={t("Share")}
          className="inline-flex items-center justify-center size-10 cursor-pointer text-ds-black"
        >
          <ShareV2Icon className="size-6" />
        </button>
      )}
      {onBookmark === null ? null : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onBookmark();
          }}
          aria-label={isBookmarked ? t("Saved") : t("Bookmark")}
          aria-pressed={isBookmarked}
          className="inline-flex items-center justify-center size-10 cursor-pointer text-ds-black"
        >
          {isBookmarked ? (
            <BookMarkFillIcon className="size-6" />
          ) : (
            <BookMarkSimpleIcon className="size-6" />
          )}
        </button>
      )}
    </div>
  );
}

function NewBadge(): React.JSX.Element {
  const { t } = useTranslation();
  // `ml-auto` pins the pill to the right of its row, including the row where
  // it stands alone (no journal name).
  return (
    <span className="ml-auto shrink-0 inline-flex items-center rounded-full bg-ds-strawberry-500 px-1.5 py-1 text-caption font-medium leading-none text-ds-white">
      {t("New")}
    </span>
  );
}

function PlayingBadge() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-ds-moss-100 py-0.75 pr-2.5 pl-2 text-caption font-medium text-ds-moss-800">
      <IconHeadphones stroke={1.25} className="size-4" />
      {t("Playing")}
    </span>
  );
}

// ── Component ───────────────────────────────────────────────────────────

function PublicationCard({
  title,
  journalName,
  author,
  date,
  meta,
  metaVariant,
  access,
  recommendation,
  isNew,
  isInPlayer,
  isBookmarked,
  onBookmark,
  onShare,
  onClick,
  className,
  imageUrl,
  description,
  onJournalClick,
  onRecommendationClick,
}: PublicationCardProps) {
  // Same w-6 footprint as the room logo so the two line up in the card's left
  // gutter. The box is one text-footnote line tall (h-5 = 20px) so the icon
  // centres on the first line when a long journal name wraps to two lines.
  const journalTile = (
    <span className="inline-flex items-center justify-center w-6 h-5 shrink-0 rounded-lg border-ds-black/8 text-ds-slate-600">
      <IconBook2 stroke={1.5} className="size-4" />
    </span>
  );
  const journalLine =
    journalName === null ? null : (
      <div className="flex items-start gap-1 text-footnote min-w-0 flex-1">
        {/* The tile shares the journal link when one is set. */}
        {onJournalClick === null ? (
          journalTile
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onJournalClick();
            }}
            className="flex shrink-0 cursor-pointer"
          >
            {journalTile}
          </button>
        )}
        {/* "In <journal>" is one clamped block: a wrapped journal name flows
            back under "In". The name is inline text (not a <button>, which is
            an atomic inline that can't break across lines). */}
        <span className="line-clamp-2 min-w-0">
          <SourceLabel
            source={{ kind: "in", name: journalName }}
            onNameClick={onJournalClick}
          />
        </span>
      </div>
    );

  // The "New" pill shares the journal row instead of floating over it: a long
  // journal name then wraps against the pill, and can never run underneath it.
  const journalRow =
    journalName === null && !isNew ? null : (
      <div className="flex items-start gap-2 w-full">
        {journalLine}
        {isNew ? <NewBadge /> : null}
      </div>
    );

  const authorLine =
    author === "" ? null : (
      <span className="text-caption font-normal text-ds-slate-600">
        {author}
      </span>
    );

  const metaInfo = (
    // Wraps when the article-type tag is long (e.g. "Revue systématique et
    // méta-analyse"): the date + access stay together and drop to a new line
    // instead of crushing the tag.
    <div className="flex flex-wrap gap-1  items-center text-caption text-ds-slate-600 min-w-0 flex-1">
      <Tag
        size="sm"
        variant={metaVariant}
        state="idle"
        icon={null}
        className="shrink-0"
      >
        {meta}
      </Tag>
      <div className="flex gap-1 items-center whitespace-nowrap">
        <span className="font-normal">· {date} ·</span>
        <AccessBadge access={access} />
      </div>
      {isInPlayer ? <PlayingBadge /> : null}
    </div>
  );

  // From `md` the hover background gets 12px of room on every side, inside
  // the card's own box: a negative margin would push the background past the
  // column that holds the list.
  const rootCls = cn(
    "bg-white flex flex-col items-start w-full gap-2 font-dm-sans transition-colors md:p-3 md:rounded-2xl md:hover:bg-ds-slate-50",
    onClick === null ? null : "cursor-pointer text-left",
    className
  );

  const content = (
    <>
      {recommendation === null ? null : (
        <RecommendationBadge
          value={recommendation}
          onClick={onRecommendationClick}
        />
      )}
      {/* Journal always leads the card body, above the hero image. */}
      {journalRow}
      <div className="flex flex-col gap-2 w-full">
        {imageUrl === null ? null : (
          <div className="w-full h-42 rounded-xl overflow-hidden bg-ds-slate-100 border border-ds-slate-100">
            <img src={imageUrl} alt="" className="size-full object-cover" />
          </div>
        )}
        <div className="flex flex-col gap-0.5 w-full">
          <div className="flex flex-col gap-1 w-full">
            <p
              className={cn(
                "m-0 w-full font-dm-sans font-semibold text-heading-xs text-ds-black",
                // The image already provides breathing room above the title;
                // without it, tighten the gap to the journal line.
                imageUrl === null ? null : "mt-1"
              )}
            >
              {title}
            </p>
            {description === null ? null : (
              <p className="m-0 w-full text-footnote font-normal text-ds-slate-700 line-clamp-3">
                {description}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1 w-full">
            {authorLine}
            <div className="flex items-center justify-between gap-2 min-h-10 w-full">
              {metaInfo}
              <ActionRow
                isBookmarked={isBookmarked}
                onBookmark={onBookmark}
                onShare={onShare}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );

  if (onClick === null) {
    return <div className={rootCls}>{content}</div>;
  }
  // A clickable card cannot be a real <button>: it contains interactive
  // children (journal link, bookmark/share, recommendation), and a <button>
  // inside a <button> is invalid DOM. Use a role="button" div with keyboard
  // activation so accessibility is preserved without the nesting violation.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        // A key pressed on a child button (bookmark, share) is that
        // button's, not the card's.
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={rootCls}
    >
      {content}
    </div>
  );
}

export {
  AccessBadge,
  PlayingBadge,
  PublicationCard,
  type PublicationCardProps,
  type Recommendation as PublicationCardRecommendation,
};
